import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { User, Phone, Building2, Calendar, Shield } from "lucide-react";

const roleLabels: Record<string, string> = {
  admin: "مدير حضانة",
  teacher: "معلم",
  parent: "ولي أمر",
  super_admin: "مشرف عام",
  support: "دعم فني",
  accountant: "محاسب",
  developer: "مطوّر",
  call_center: "مركز اتصال",
  supervisor: "مشرف",
};

const roleColors: Record<string, string> = {
  admin: "bg-blue-100 text-blue-800",
  teacher: "bg-green-100 text-green-800",
  parent: "bg-purple-100 text-purple-800",
  super_admin: "bg-amber-100 text-amber-800",
  support: "bg-cyan-100 text-cyan-800",
  accountant: "bg-pink-100 text-pink-800",
  developer: "bg-indigo-100 text-indigo-800",
  call_center: "bg-teal-100 text-teal-800",
  supervisor: "bg-orange-100 text-orange-800",
};

interface UserDetailsDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  user: any | null;
}

const UserDetailsDialog = ({ open, onOpenChange, user }: UserDetailsDialogProps) => {
  if (!user) return null;

  const fields = [
    { icon: User, label: "الاسم", value: user.full_name || "—" },
    { icon: Phone, label: "الهاتف", value: user.phone || "—", dir: "ltr" as const },
    { icon: Building2, label: "الحضانة", value: user.nurseries?.name || "بدون حضانة" },
    { icon: Calendar, label: "تاريخ التسجيل", value: format(new Date(user.created_at), "dd MMMM yyyy - HH:mm", { locale: ar }) },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            تفاصيل المستخدم
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Avatar & Name */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
              {user.full_name?.charAt(0) || "؟"}
            </div>
            <div>
              <p className="font-semibold text-lg">{user.full_name || "بدون اسم"}</p>
              <p className="text-xs text-muted-foreground">ID: {user.user_id?.slice(0, 8)}...</p>
            </div>
          </div>

          {/* Fields */}
          <div className="space-y-3">
            {fields.map((f) => (
              <div key={f.label} className="flex items-center gap-3 text-sm">
                <f.icon className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground w-24">{f.label}:</span>
                <span className="font-medium" dir={f.dir}>{f.value}</span>
              </div>
            ))}
          </div>

          {/* Roles */}
          <div className="flex items-start gap-3 text-sm">
            <Shield className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
            <span className="text-muted-foreground w-24">الأدوار:</span>
            <div className="flex gap-1 flex-wrap">
              {user.roles?.length ? user.roles.map((r: string) => (
                <span key={r} className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleColors[r] || "bg-muted"}`}>
                  {roleLabels[r] || r}
                </span>
              )) : <span className="text-muted-foreground">بدون أدوار</span>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailsDialog;
